import dayjs from '@/lib/dayjs';
import { PUBLISH_DATE_BUFFER_DAYS } from '@/utils/constants/sermons';

/**
 * Converts a user's selected local publish date into the UTC timestamp the CMS expects.
 *
 * The CMS publishes content at the UTC timestamp it receives and always expects it to be at
 * 12:00 AM UTC (`YYYY-MM-DDT00:00:00Z`). To make the sermon publish on the date the user picked
 * **in their own timezone**, the date is shifted forward by one day when the selected timezone is
 * west of UTC (a negative UTC offset); timezones at or east of UTC send the selected date as-is.
 *
 * @example
 * // CDT (UTC−5) selects May 2 -> shifted to May 3 so it publishes May 2 7:00 PM CDT
 * resolvePublishTimestamp(new Date(2026, 4, 2), 'America/Chicago'); // '2026-05-03T00:00:00Z'
 * @example
 * // AEST (UTC+10) selects May 2 -> sent as-is, publishes May 2 10:00 AM AEST
 * resolvePublishTimestamp(new Date(2026, 4, 2), 'Australia/Sydney'); // '2026-05-02T00:00:00Z'
 *
 * @param selectedDate - The calendar date the user selected (read as its local wall-clock Y/M/D).
 * @param timezone - The IANA timezone the user selected (e.g. `America/Chicago`).
 * @returns A `YYYY-MM-DDT00:00:00Z` UTC timestamp to send to the CMS.
 */
export function resolvePublishTimestamp(selectedDate: Date, timezone: string): string {
  // The wall-clock date the user picked — not a UTC conversion of the JS Date's instant.
  const selectedYmd = dayjs(selectedDate).format('YYYY-MM-DD');

  // Offset of the selected timezone on that date (DST-aware). West of UTC => negative.
  const offsetMinutes = dayjs.tz(selectedYmd, timezone).utcOffset();
  const shiftDays = offsetMinutes < 0 ? 1 : 0;

  const sentYmd = dayjs.utc(selectedYmd).add(shiftDays, 'day').format('YYYY-MM-DD');
  return `${sentYmd}T00:00:00Z`;
}

/**
 * Earliest calendar day a sermon may be scheduled to publish, returned as a local-midnight `Date`.
 *
 * Computed relative to "today" in the selected publish timezone so the buffer is enforced against
 * the same clock the publish timestamp resolves against (see {@link resolvePublishTimestamp}). When
 * the browser timezone and the selected timezone differ, "today" can be a different calendar day in
 * each, so measuring the buffer in the publish timezone is what keeps the picker's `minDate` and the
 * schema validation honest. Falls back to the browser's local day when no timezone is provided.
 *
 * The returned `Date` is the bare calendar day at local midnight: the picked `publishDate` and
 * Mantine's calendar cells are both bare days in the browser frame, so a local-midnight value
 * compares calendar-day-to-calendar-day. The timezone only shifts which day counts as "today + buffer".
 *
 * @param timezone - The IANA timezone the user selected (e.g. `America/Chicago`). Optional.
 * @returns A local-midnight `Date` for the earliest selectable publish day.
 */
export function getEarliestPublishDate(timezone?: string): Date {
  const base = timezone ? dayjs().tz(timezone) : dayjs();
  const ymd = base.add(PUBLISH_DATE_BUFFER_DAYS, 'day').format('YYYY-MM-DD');
  return dayjs(ymd).startOf('day').toDate();
}

/**
 * Converts a stored publish timestamp back into the local-midnight `Date` the date picker expects.
 *
 * This is the inverse of {@link resolvePublishTimestamp}: given the UTC timestamp the CMS stored and
 * the IANA timezone the user originally selected, it recovers the wall-clock calendar day the user
 * picked (by expressing the instant in that timezone). Because the timezone shift baked in by
 * `resolvePublishTimestamp` is undone by reading the instant back in the same timezone, both
 * west-of-UTC and east-of-UTC zones round-trip to the originally selected day.
 *
 * The returned `Date` is the bare calendar day at local midnight, matching how the picker and
 * {@link getEarliestPublishDate} model dates, so the two compare calendar-day-to-calendar-day.
 *
 * @example
 * // Stored '2026-05-03T00:00:00Z' for CDT (UTC−5) -> user originally picked May 2
 * getScheduledPublishDate('2026-05-03T00:00:00Z', 'America/Chicago'); // local midnight, May 2 2026
 * @example
 * // Stored '2026-05-02T00:00:00Z' for AEST (UTC+10) -> user originally picked May 2
 * getScheduledPublishDate('2026-05-02T00:00:00Z', 'Australia/Sydney'); // local midnight, May 2 2026
 *
 * @param timestamp - The stored publish timestamp (e.g. `2026-05-03T00:00:00Z`). Optional.
 * @param timezone - The IANA timezone the user selected (e.g. `America/Chicago`). Optional.
 * @returns A local-midnight `Date` for the originally selected day, or `null` when either input is missing.
 */
export function getScheduledPublishDate(timestamp?: string | null, timezone?: string | null): Date | null {
  if (!timestamp || !timezone) {
    return null;
  }

  const ymd = dayjs.utc(timestamp).tz(timezone).format('YYYY-MM-DD');
  return dayjs(ymd).startOf('day').toDate();
}
